import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, CheckCircle2, AlertTriangle, PauseCircle, Archive, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const STATE = {
  active: { icon: Loader2, cls: "text-[#007AFF]", spin: true },
  complete: { icon: CheckCircle2, cls: "text-emerald-400" },
  paused: { icon: PauseCircle, cls: "text-[#FFB020]" },
  approval: { icon: ShieldCheck, cls: "text-[#FFB020]" },
  error: { icon: AlertTriangle, cls: "text-[#FF3B30]" },
  idle: { icon: Archive, cls: "text-neutral-400" },
};

export const CaseProgress = ({ caseId }) => {
  const [p, setP] = useState(null);

  useEffect(() => {
    let alive = true;
    let timer;
    const tick = async () => {
      try {
        const r = await api.get(`/cases/${caseId}/progress`);
        if (alive) setP(r.data);
      } catch { /* keep last known state */ }
      if (alive) timer = setTimeout(tick, 3000);
    };
    tick();
    return () => { alive = false; clearTimeout(timer); };
  }, [caseId]);

  if (!p || p.files.total === 0) return null;
  const { total, sealed, failed } = p.files;
  const pct = Math.round((sealed / total) * 100);
  const s = STATE[p.state] || STATE.idle;
  const Icon = s.icon;

  return (
    <div data-testid="case-progress" className="mb-6 bg-[#141414] border border-white/10 rounded-md px-5 py-4">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="font-mono text-sm text-neutral-100" data-testid="case-progress-files">
          <span className="text-white font-semibold">{sealed}</span> of {total} files sealed
          {failed > 0 && <span className="ml-2 text-[#FF3B30]" data-testid="case-progress-failed">· {failed} failed</span>}
        </div>
        <div className={`flex items-center gap-2 text-[13px] ${s.cls}`} data-testid="case-progress-step">
          <Icon className={`w-4 h-4 ${s.spin ? "animate-spin" : ""}`} /> {p.step}
        </div>
      </div>
      <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden" data-testid="case-progress-bar">
        <div className="h-full bg-[#007AFF] transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>
      {p.approval && <ApprovalBar caseId={caseId} approval={p.approval}
        onDone={() => setP((x) => ({ ...x, approval: null, step: `${p.approval.label}: starting`, state: "active" }))} />}
    </div>
  );
};

const plural = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? "" : "s"}`;

const ApprovalBar = ({ caseId, approval, onDone }) => {
  const [busy, setBusy] = useState(false);
  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/cases/${caseId}/approve/${approval.stage}`);
      toast.success(`Approved: ${approval.label}`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not approve this step");
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="case-progress-approval" className="mt-4 flex items-center justify-between gap-3 flex-wrap rounded-md border border-[#FFB020]/40 bg-[#FFB020]/10 px-4 py-3">
      <div className="text-[13px] text-neutral-200" data-testid="case-progress-approval-text">
        <span className="font-medium text-[#FFB020]">{approval.label}</span>: {plural(approval.items, "item")}, about{" "}
        <span className="font-semibold text-white">{plural(approval.credits, "credit")}</span> (estimate). Nothing runs until you approve.
      </div>
      <button onClick={approve} disabled={busy} data-testid="case-progress-approve-btn"
        className="flex items-center gap-2 border border-[#FFB020]/60 text-[#FFB020] text-sm rounded-sm px-3 py-2 hover:bg-[#FFB020]/10 disabled:opacity-60 transition-colors">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Approve and run
      </button>
    </div>
  );
};
